import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useQueryParams } from '@/support/Hooks/useQueryParams/useQueryParams';
import { _isNotEmpty } from '@/littledash';
import { NoDataMessage } from '@/components/NoDataCard/NoDataCard';
import Spinner from '@/components/UI/Spinner/Spinner';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import AgentCard from '@/components/Agents/AgentCard/AgentCard';
import RecentSessionsPanel from '@/components/Agents/RecentSessionsPanel/RecentSessionsPanel';
import SessionsTable from '@/components/Agents/SessionsTable/SessionsTable';
import { LuSparkle } from 'react-icons/lu';
import styles from './AgentsPage.module.scss';

const TAB_QUERY_PARAM = 'tab';
const TABS = ['agents', 'sessions'] as const;

const getTabIndex = (params: URLSearchParams): number => {
  const tab = params.get(TAB_QUERY_PARAM);
  const index = TABS.indexOf(tab as (typeof TABS)[number]);
  return index >= 0 ? index : 0;
};

const AgentsPage: FC = () => {
  const navigate = useNavigate();
  const queryParams = useQueryParams();

  const { loading, response } = useApiHook({
    endpoint: 'GET /api/v1/ai/agents',
    options: {
      invokeOnInit: true,
      onError: {
        slug: 'agents-page',
        toast: false,
        throw: false,
      },
    },
  });

  const agents = response?.type === 'success' ? response.body : [];
  const [activeTab, setActiveTab] = useState(() => getTabIndex(queryParams));

  useEffect(() => {
    const tabFromUrl = getTabIndex(queryParams);
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [queryParams]);

  useEffect(() => {
    const currentTab = queryParams.get(TAB_QUERY_PARAM);
    if (currentTab !== TABS[activeTab]) {
      const updatedParams = new URLSearchParams(queryParams.toString());
      updatedParams.set(TAB_QUERY_PARAM, TABS[activeTab]);
      navigate({ search: updatedParams.toString() });
    }
  }, [activeTab]);

  return (
    <div className="ph4 pb4" data-test-component="AgentsPage" data-test-element="container">
      <div className="pt3 pb4" data-test-element="header">
        <div className={styles.titleRow}>
          <h1 className="f3 normal ma0" data-test-element="title">
            In Vivo AI
          </h1>
          <LuSparkle size={20} className="blue" />
        </div>
        <p className="f5 lh-copy dark-gray mt3 mb0" data-test-element="subtitle">
          Purpose-built AI agents for your In Vivo studies. Each agent works with your live study data and returns
          results you can review and apply directly.
        </p>
      </div>

      <Tabs outerState={[activeTab, setActiveTab]}>
        <div className="bb b--moon-gray">
          <Tab className="f4" testId="agents-tab">
            Agents
          </Tab>
          <Tab className="f4" testId="sessions-tab">
            Sessions
          </Tab>
        </div>

        <Panel className="pt4">
          <div className={styles.content}>
            {loading ? (
              <div className="flex justify-center items-center w-100 pv5" data-testid="agents-page-loading">
                <Spinner size="default" color="blue" />
              </div>
            ) : _isNotEmpty(agents) ? (
              <div className={styles.grid}>
                {agents.map((agent) => (
                  <AgentCard key={agent.api_id} agent={agent} />
                ))}
              </div>
            ) : (
              <div className="w-100">
                <NoDataMessage
                  title="No agents available"
                  text="AI agents will appear here when enabled for your team."
                  dark
                />
              </div>
            )}
            <RecentSessionsPanel onViewAllSessions={() => setActiveTab(1)} />
          </div>
        </Panel>

        <Panel className="pt4">
          <SessionsTable agents={agents} />
        </Panel>
      </Tabs>
    </div>
  );
};

export default AgentsPage;
